import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import XGSNavLink from "../xgs-nav-link/xgsNavLink";
import Loading from "../loading/loading";
import StatusPill from "../status-pill/statusPill";
import TimestampDisplay from "../timestamp-display/timestampDisplay";
import { Routes } from "../../app/route/RoutesConfig";
import { DockEmployeeStatus, DockTag } from "../../app/data/dock-scan/models";
import { formatSessionDuration } from "../../app/data/dock-scan/dockLogMappers";
import { useDockEmployee } from "../../features/dock-scan-management/hooks/useDockScanQueries";
import { userSelector } from "../../slices/user/userSlice";
import "./dockUserCard.scss";

const AVATAR_COLORS = [
  "#6366f1",
  "#8b5cf6",
  "#0d9488",
  "#2563eb",
  "#059669",
  "#d97706",
  "#db2777",
  "#7c3aed",
];

export function getDockUserInitials(
  firstName?: string | null,
  lastName?: string | null,
  employeeId?: string
): string {
  const first = (firstName || "").trim().charAt(0);
  const last = (lastName || "").trim().charAt(0);
  if (first || last) return `${first}${last}`.toUpperCase();
  if (employeeId) return employeeId.slice(-2).toUpperCase();
  return "?";
}

export function getDockUserAvatarColor(employeeId: string): string {
  let hash = 0;
  for (let i = 0; i < employeeId.length; i++) {
    hash = (hash * 31 + employeeId.charCodeAt(i)) | 0;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export interface DockUserSessionSummary {
  equipmentId?: string;
  sessionDurationMs?: number;
  scansTotal?: number;
  scansFailure?: number;
}

export interface DockUserCardProps {
  employeeId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  status?: DockEmployeeStatus | null;
  tags?: DockTag[];
  terminalNumber?: number | null;
  terminalName?: string | null;
  isOnDock?: boolean;
  session?: DockUserSessionSummary;
  updatedAt?: string | null;
  showHoverCard?: boolean;
  /** When false, hover popover can extend outside scroll containers (e.g. table overflow) */
  keepTooltipInside?: boolean;
  linkToProfile?: boolean;
  className?: string;
}

interface AvatarProps {
  initials: string;
  color: string;
  size?: "sm" | "lg";
}

const DockUserAvatar: React.FC<AvatarProps> = ({ initials, color, size = "sm" }) => (
  <span
    className={`xgs-dock-user-card__avatar xgs-dock-user-card__avatar--${size}`}
    style={{ backgroundColor: color }}
    aria-hidden
  >
    {initials}
  </span>
);

interface PopoverContentProps {
  employeeId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  status?: DockEmployeeStatus | null;
  tags?: DockTag[];
  terminalNumber?: number | null;
  terminalName?: string | null;
  isOnDock?: boolean;
  session?: DockUserSessionSummary;
  updatedAt?: string | null;
  employee: ReturnType<typeof useDockEmployee>["data"];
  isLoading: boolean;
  dockUsersPath: string;
}

const PopoverContent: React.FC<PopoverContentProps> = ({
  employeeId,
  firstName,
  lastName,
  email,
  status,
  tags,
  terminalNumber,
  terminalName,
  isOnDock,
  session,
  updatedAt,
  employee,
  isLoading,
  dockUsersPath,
}) => {
  const resolvedFirst = employee?.firstName ?? firstName ?? "";
  const resolvedLast = employee?.lastName ?? lastName ?? "";
  const displayName =
    [resolvedFirst, resolvedLast].filter(Boolean).join(" ") || `Employee ${employeeId}`;
  const resolvedEmail = employee?.email ?? email;
  const resolvedStatus = employee?.status ?? status;
  const resolvedTags = employee?.tags ?? tags ?? [];
  const resolvedTerminal = employee?.terminalNumber ?? terminalNumber;
  const resolvedUpdated = employee?.updatedAt ?? updatedAt;
  const initials = getDockUserInitials(resolvedFirst, resolvedLast, employeeId);
  const color = getDockUserAvatarColor(employeeId);

  // Reposition after async content loads (reactjs-popup only recalculates on resize)
  useEffect(() => {
    if (isLoading) return;
    window.dispatchEvent(new Event("resize"));
  }, [isLoading, employee, resolvedTags.length]);

  const sessionLine = session?.equipmentId
    ? [
        session.equipmentId,
        session.sessionDurationMs != null ? formatSessionDuration(session.sessionDurationMs) : null,
        session.scansTotal != null
          ? `${session.scansTotal} scan${session.scansTotal === 1 ? "" : "s"}${
              (session.scansFailure ?? 0) > 0 ? ` (${session.scansFailure} failed)` : ""
            }`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="xgs-dock-user-card-popover__content">
      <div className="xgs-dock-user-card-popover__header">
        <DockUserAvatar initials={initials} color={color} size="lg" />
        <div className="xgs-dock-user-card-popover__header-text">
          <span className="xgs-dock-user-card-popover__title">{displayName}</span>
          <span className="xgs-dock-user-card-popover__role">Dock Operator</span>
          <div className="xgs-dock-user-card-popover__status-row">
            {resolvedStatus && (
              <StatusPill
                label={resolvedStatus}
                variant={resolvedStatus === "ACTIVE" ? "success" : "inactive"}
              />
            )}
            {isOnDock != null && (
              <StatusPill
                label={isOnDock ? "ON DOCK" : "OFF DOCK"}
                variant={isOnDock ? "live" : "neutral"}
              />
            )}
          </div>
        </div>
      </div>

      <div className="xgs-dock-user-card-popover__divider" />

      {isLoading && !employee && !firstName && !lastName ? (
        <div className="xgs-dock-user-card-popover__loading">
          <Loading isLoading />
        </div>
      ) : (
        <div className="xgs-dock-user-card-popover__body">
          <div className="xgs-dock-user-card-popover__row">
            <span className="xgs-dock-user-card-popover__label">Employee ID</span>
            <span className="xgs-dock-user-card-popover__value"># {employeeId}</span>
          </div>
          {resolvedEmail && (
            <div className="xgs-dock-user-card-popover__row">
              <span className="xgs-dock-user-card-popover__label">Email</span>
              <span className="xgs-dock-user-card-popover__value">{resolvedEmail}</span>
            </div>
          )}
          {(resolvedTerminal != null || terminalName) && (
            <div className="xgs-dock-user-card-popover__row">
              <span className="xgs-dock-user-card-popover__label">Terminal</span>
              <span className="xgs-dock-user-card-popover__value">
                {[terminalName, resolvedTerminal != null ? `T${resolvedTerminal}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </div>
          )}
          {resolvedTags.length > 0 && (
            <div className="xgs-dock-user-card-popover__row">
              <span className="xgs-dock-user-card-popover__label">Tags</span>
              <span className="xgs-dock-user-card-popover__tags">
                {resolvedTags.map((tag) => (
                  <span key={tag.id} className="xgs-dock-user-card-popover__tag">
                    {tag.name}
                  </span>
                ))}
              </span>
            </div>
          )}
          {sessionLine && (
            <div className="xgs-dock-user-card-popover__session">{sessionLine}</div>
          )}
        </div>
      )}

      <div className="xgs-dock-user-card-popover__footer">
        {resolvedUpdated && (
          <span className="xgs-dock-user-card-popover__updated">
            Updated <TimestampDisplay value={resolvedUpdated} format="local" />
          </span>
        )}
        <XGSNavLink className="xgs-dock-user-card-popover__more-link" to={dockUsersPath}>
          View dock user →
        </XGSNavLink>
      </div>
    </div>
  );
};

const DockUserCard: React.FC<DockUserCardProps> = ({
  employeeId,
  firstName,
  lastName,
  email,
  status,
  tags,
  terminalNumber,
  terminalName,
  isOnDock,
  session,
  updatedAt,
  showHoverCard = true,
  keepTooltipInside = false,
  linkToProfile = true,
  className,
}) => {
  const history = useHistory();
  const userState = useSelector(userSelector);
  const dockUsersPath = `${Routes.dockUsers.list}?employeeId=${encodeURIComponent(employeeId)}`;

  const [shouldFetch, setShouldFetch] = useState(false);
  const { data: employee, isLoading } = useDockEmployee(shouldFetch ? employeeId : undefined);

  // Trigger uses only props — never fetched data — so hover fetch cannot remount/reposition Popup
  const cellHasName = !!(firstName || lastName);
  const displayName = cellHasName
    ? `${firstName || ""} ${lastName || ""}`.trim()
    : employeeId;
  const initials = useMemo(
    () => getDockUserInitials(firstName, lastName, employeeId),
    [firstName, lastName, employeeId]
  );
  const color = useMemo(() => getDockUserAvatarColor(employeeId), [employeeId]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!linkToProfile) return;
    const terminalPath = userState.activeTerminal?.id ? `/${userState.activeTerminal.id}` : "";
    history.push(`${terminalPath}${dockUsersPath}`);
  };

  const trigger = (
    <span
      className={`xgs-dock-user-card${linkToProfile ? " xgs-dock-user-card--clickable" : ""}${
        className ? ` ${className}` : ""
      }`}
      tabIndex={0}
      role={linkToProfile ? "link" : undefined}
      onClick={handleTriggerClick}
      onKeyDown={(e) => {
        if (linkToProfile && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleTriggerClick(e as unknown as React.MouseEvent);
        }
      }}
    >
      <DockUserAvatar initials={initials} color={color} size="sm" />
      <span className="xgs-dock-user-card__text">
        <span className="xgs-dock-user-card__name">{displayName}</span>
        {cellHasName && <span className="xgs-dock-user-card__id">{employeeId}</span>}
      </span>
    </span>
  );

  if (!showHoverCard) return trigger;

  return (
    <Popup
      trigger={trigger}
      position="bottom left"
      on="hover"
      arrow={false}
      mouseEnterDelay={200}
      mouseLeaveDelay={250}
      keepTooltipInside={keepTooltipInside}
      onOpen={() => setShouldFetch(true)}
      contentStyle={{
        width: "320px",
        padding: 0,
        borderRadius: 8,
        border: "1px solid #e5e7eb",
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
        zIndex: 1100,
      }}
    >
      <PopoverContent
        employeeId={employeeId}
        firstName={firstName}
        lastName={lastName}
        email={email}
        status={status}
        tags={tags}
        terminalNumber={terminalNumber}
        terminalName={terminalName}
        isOnDock={isOnDock}
        session={session}
        updatedAt={updatedAt}
        employee={employee}
        isLoading={isLoading}
        dockUsersPath={dockUsersPath}
      />
    </Popup>
  );
};

export default DockUserCard;
